import { createTheme, ThemeOptions } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

// Modern Color Palette - Based on 2024 Design Trends
const colorPalette = {
  // Primary Brand Colors - Modern Purple/Blue Gradient
  primary: {
    50: '#f0f4ff',
    100: '#e0e7ff',
    200: '#c7d2fe',
    300: '#a5b4fc',
    400: '#818cf8',
    500: '#6366f1', // Main brand color
    600: '#4f46e5',
    700: '#4338ca',
    800: '#3730a3',
    900: '#312e81',
  },
  
  // Secondary Colors - Complementary Teal
  secondary: {
    50: '#f0fdfa',
    100: '#ccfbf1',
    200: '#99f6e4',
    300: '#5eead4',
    400: '#2dd4bf',
    500: '#14b8a6', // Main secondary
    600: '#0d9488',
    700: '#0f766e',
    800: '#115e59',
    900: '#134e4a',
  },
  
  // Neutral Grays - Warm and Modern
  neutral: {
    50: '#fafaf9',
    100: '#f5f5f4',
    200: '#e7e5e4',
    300: '#d6d3d1',
    400: '#a8a29e',
    500: '#78716c',
    600: '#57534e',
    700: '#44403c',
    800: '#292524',
    900: '#1c1917',
  },
  
  // Success Colors - Modern Green
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
  },
  
  // Warning Colors - Modern Amber
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
  },
  
  // Error Colors - Modern Red
  error: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },
};

// Typography System - Modern and Readable
const typography = {
  fontFamily: [
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
  ].join(','),
  
  // Display Typography
  h1: {
    fontSize: '3.5rem',
    fontWeight: 800,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
  },
  h2: {
    fontSize: '2.5rem',
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  h3: {
    fontSize: '2rem',
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 500,
    lineHeight: 1.5,
  },
  h6: {
    fontSize: '1.125rem',
    fontWeight: 500,
    lineHeight: 1.5,
  },
  
  // Body Typography
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.6,
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.5,
  },
  
  // UI Typography
  button: {
    fontSize: '0.875rem',
    fontWeight: 500,
    textTransform: 'none' as const,
    letterSpacing: '0.02em',
  },
  caption: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.4,
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
  },
};

// Spacing System - 8px Grid
const spacing = (factor: number) => `${8 * factor}px`;

// Shadow System - Modern Elevation
const shadows = [
  'none',
  '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0 35px 60px -12px rgba(0, 0, 0, 0.3)',
  // Glassmorphism shadows
  '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
  '0 8px 32px 0 rgba(99, 102, 241, 0.2)',
  // Neumorphism shadows
  '8px 8px 16px #d1d9e6, -8px -8px 16px #ffffff',
  '12px 12px 24px #d1d9e6, -12px -12px 24px #ffffff',
  '16px 16px 32px #d1d9e6, -16px -16px 32px #ffffff',
  // Floating shadows
  '0 4px 20px rgba(99, 102, 241, 0.15)',
  '0 8px 30px rgba(99, 102, 241, 0.2)',
  '0 12px 40px rgba(99, 102, 241, 0.25)',
  // Card shadows
  '0 2px 8px rgba(0, 0, 0, 0.08)',
  '0 4px 16px rgba(0, 0, 0, 0.12)',
  '0 8px 24px rgba(0, 0, 0, 0.15)',
  '0 16px 32px rgba(0, 0, 0, 0.18)',
  // Interactive shadows
  '0 2px 4px rgba(0, 0, 0, 0.1), 0 8px 16px rgba(0, 0, 0, 0.1)',
  '0 4px 8px rgba(0, 0, 0, 0.12), 0 16px 24px rgba(0, 0, 0, 0.12)',
  '0 8px 16px rgba(0, 0, 0, 0.15), 0 24px 32px rgba(0, 0, 0, 0.15)',
  '0 16px 24px rgba(0, 0, 0, 0.18), 0 32px 48px rgba(0, 0, 0, 0.18)',
];

// Border Radius System
const borderRadius = {
  xs: '0.25rem',
  sm: '0.375rem',
  md: '0.5rem',
  lg: '0.75rem',
  xl: '1rem',
  '2xl': '1.5rem',
  '3xl': '2rem',
  full: '9999px',
};

// Create the modern theme
export const modernTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: colorPalette.primary[500],
      light: colorPalette.primary[300],
      dark: colorPalette.primary[700],
      contrastText: '#ffffff',
    },
    secondary: {
      main: colorPalette.secondary[500],
      light: colorPalette.secondary[300],
      dark: colorPalette.secondary[700],
      contrastText: '#ffffff',
    },
    error: {
      main: colorPalette.error[500],
      light: colorPalette.error[300],
      dark: colorPalette.error[700],
    },
    warning: {
      main: colorPalette.warning[500],
      light: colorPalette.warning[300],
      dark: colorPalette.warning[700],
    },
    success: {
      main: colorPalette.success[500],
      light: colorPalette.success[300],
      dark: colorPalette.success[700],
    },
    grey: colorPalette.neutral,
    background: {
      default: '#fafbfc',
      paper: '#ffffff',
    },
    text: {
      primary: colorPalette.neutral[900],
      secondary: colorPalette.neutral[600],
    },
  },
  
  typography,
  
  spacing,
  
  shadows: shadows as any,
  
  shape: {
    borderRadius: 12, // Default border radius
  },
  
  components: {
    // AppBar Component
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: `linear-gradient(135deg, ${colorPalette.primary[600]} 0%, ${colorPalette.primary[500]} 50%, ${colorPalette.secondary[500]} 100%)`,
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${alpha(colorPalette.primary[200], 0.2)}`,
          boxShadow: '0 8px 32px 0 rgba(99, 102, 241, 0.2)',
        },
      },
    },
    
    // Button Component
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.lg,
          textTransform: 'none',
          fontWeight: 500,
          padding: '12px 24px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(99, 102, 241, 0.25)',
          },
        },
        contained: {
          background: `linear-gradient(135deg, ${colorPalette.primary[500]} 0%, ${colorPalette.primary[600]} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${colorPalette.primary[600]} 0%, ${colorPalette.primary[700]} 100%)`,
          },
        },
        outlined: {
          borderColor: colorPalette.primary[300],
          color: colorPalette.primary[600],
          '&:hover': {
            borderColor: colorPalette.primary[500],
            backgroundColor: alpha(colorPalette.primary[50], 0.5),
          },
        },
      },
    },
    
    // Card Component
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.xl,
          border: `1px solid ${alpha(colorPalette.neutral[200], 0.5)}`,
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    
    // Paper Component
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.lg,
          border: `1px solid ${alpha(colorPalette.neutral[200], 0.3)}`,
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        },
        elevation2: {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
        },
        elevation3: {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
        },
      },
    },
    
    // TextField Component
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: borderRadius.lg,
            backgroundColor: alpha(colorPalette.neutral[50], 0.5),
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: alpha(colorPalette.neutral[50], 0.8),
            },
            '&.Mui-focused': {
              backgroundColor: '#ffffff',
              boxShadow: `0 0 0 3px ${alpha(colorPalette.primary[500], 0.1)}`,
            },
          },
        },
      },
    },
    
    // Chip Component
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.full,
          fontWeight: 500,
        },
        filled: {
          backgroundColor: alpha(colorPalette.primary[500], 0.1),
          color: colorPalette.primary[700],
          '&:hover': {
            backgroundColor: alpha(colorPalette.primary[500], 0.2),
          },
        },
      },
    },
    
    // Table Components
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.xl,
          border: `1px solid ${alpha(colorPalette.neutral[200], 0.3)}`,
          overflow: 'hidden',
        },
      },
    },
    
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(colorPalette.primary[50], 0.5),
          '& .MuiTableCell-head': {
            fontWeight: 600,
            color: colorPalette.neutral[700],
            borderBottom: `1px solid ${alpha(colorPalette.neutral[200], 0.5)}`,
          },
        },
      },
    },
    
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: alpha(colorPalette.primary[50], 0.3),
          },
          '&:last-child td': {
            borderBottom: 0,
          },
        },
      },
    },
    
    // Toolbar Component
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '72px !important',
          padding: '0 32px',
        },
      },
    },
  },
} as ThemeOptions);

// Dark theme variant
export const modernDarkTheme = createTheme({
  ...modernTheme,
  palette: {
    mode: 'dark',
    primary: {
      main: colorPalette.primary[400],
      light: colorPalette.primary[300],
      dark: colorPalette.primary[600],
      contrastText: '#ffffff',
    },
    secondary: {
      main: colorPalette.secondary[400],
      light: colorPalette.secondary[300],
      dark: colorPalette.secondary[600],
      contrastText: '#ffffff',
    },
    background: {
      default: '#0f0f23',
      paper: '#1a1a2e',
    },
    text: {
      primary: '#ffffff',
      secondary: alpha('#ffffff', 0.7),
    },
  },
  components: {
    ...modernTheme.components,
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: `linear-gradient(135deg, ${colorPalette.primary[800]} 0%, ${colorPalette.primary[700]} 50%, ${colorPalette.secondary[700]} 100%)`,
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${alpha(colorPalette.primary[400], 0.2)}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'rgba(26, 26, 46, 0.8)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha(colorPalette.primary[400], 0.2)}`,
        },
      },
    },
  },
});

export default modernTheme;
